import React from 'react';
import './curve-theme.css';

export type CaseListItemData = {
  id: string;
  status: 'pending' | 'in_progress' | 'closed';
  statusLabel: string;
  statusColor: string;
  priority?: string;
  priorityColor?: string;
  caseNo?: string;
  unit?: string;
  docNo?: string;
  subject: string;
  excerpt?: string;
  time: string;
  archived?: boolean;
};

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  orange: {
    bg: 'var(--curve-status-pending-bg, #FFF7ED)',
    color: 'var(--curve-status-pending-text, #EA580C)',
  },
  blue: {
    bg: 'var(--curve-status-inprog-bg, #EFF6FF)',
    color: 'var(--curve-status-inprog-text, #2563EB)',
  },
  gray: {
    bg: 'var(--curve-status-closed-bg, #F0FDF4)',
    color: 'var(--curve-status-closed-text, #16A34A)',
  },
};

type CaseListItemProps = {
  item: CaseListItemData;
  isSelected?: boolean;
  onSelect: (id: string) => void;
};

export function CaseListItem({ item, isSelected, onSelect }: CaseListItemProps) {
  const style = STATUS_STYLE[item.statusColor] ?? STATUS_STYLE.gray;

  return (
    <div
      onClick={() => onSelect(item.id)}
      style={{
        padding: '10px 12px',
        borderBottom: '1px solid var(--curve-border, #E5E5E5)',
        background: isSelected
          ? 'var(--curve-primary-soft, #F0FDF4)'
          : 'var(--curve-bg-white, #FFFFFF)',
        cursor: 'pointer',
        transition: 'background var(--curve-transition, 150ms ease)',
        borderLeft: isSelected
          ? '3px solid var(--curve-primary, #22C55E)'
          : '3px solid transparent',
        boxSizing: 'border-box',
      }}
      onMouseEnter={(e) => {
        if (!isSelected)
          (e.currentTarget as HTMLDivElement).style.background =
            'var(--curve-bg-gray, #F5F5F5)';
      }}
      onMouseLeave={(e) => {
        if (!isSelected)
          (e.currentTarget as HTMLDivElement).style.background =
            'var(--curve-bg-white, #FFFFFF)';
      }}
    >
      {/* 第一行：狀態 badge + 優先度點 + 時間 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--curve-space-xs, 4px)',
          marginBottom: '4px',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '1px 6px',
            borderRadius: 'var(--curve-radius-sm, 4px)',
            fontSize: 'var(--curve-text-xxs, 11px)',
            fontWeight: 600,
            background: style.bg,
            color: style.color,
            flexShrink: 0,
          }}
        >
          {item.statusLabel}
        </span>

        {item.priority && item.priority !== '未設' && (
          <span
            style={{
              display: 'inline-block',
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: item.priorityColor ?? 'var(--curve-border, #E5E5E5)',
              flexShrink: 0,
            }}
            title={`優先度：${item.priority}`}
          />
        )}

        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 'var(--curve-text-xxs, 11px)',
            color: 'var(--curve-text-placeholder, #94A3B8)',
            flexShrink: 0,
          }}
        >
          {item.time}
        </span>
      </div>

      {/* 第二行：案件編號 + 單位 */}
      {(item.caseNo || item.unit) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--curve-space-xs, 4px)',
            marginBottom: '2px',
          }}
        >
          {item.caseNo && (
            <span
              style={{
                fontSize: 'var(--curve-text-xs, 12px)',
                color: 'var(--curve-text-muted, #64748B)',
                fontWeight: 500,
              }}
            >
              {item.caseNo}
            </span>
          )}
          {item.unit && (
            <span
              style={{
                fontSize: 'var(--curve-text-xs, 12px)',
                color: 'var(--curve-text-placeholder, #94A3B8)',
              }}
            >
              {item.unit}
            </span>
          )}
        </div>
      )}

      {/* 第三行：文號（狀態色） */}
      {item.docNo && (
        <div style={{ marginBottom: '3px' }}>
          <span
            style={{
              fontSize: 'var(--curve-text-xs, 12px)',
              color: style.color,
              fontWeight: 500,
            }}
          >
            {item.docNo}
          </span>
        </div>
      )}

      {/* 第四行：主旨 */}
      <div
        style={{
          fontSize: 'var(--curve-text-small, 13px)',
          color: 'var(--curve-text-dark, #1E293B)',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.subject}
      </div>

      {/* 第五行：摘要 */}
      {item.excerpt && (
        <div
          style={{
            fontSize: 'var(--curve-text-xs, 12px)',
            color: 'var(--curve-text-placeholder, #94A3B8)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: '2px',
          }}
        >
          {item.excerpt}
        </div>
      )}
    </div>
  );
}
